import { fetchAuthSession } from "@aws-amplify/auth"
/**
 * API client for the CV automation service
 * This file contains functions to call external APIs
 */

/**
 * Interface representing a CV item returned from the API
 */
export interface CvItem {
  collection_id: string;
  user_id: string;
  mime: string;
  original_file_name: string;
  upload_time: string;
  cv_id: string;
  last_update_time: string;
}

/**
 * Interface representing a summary item for display in the UI
 */
export interface SummaryItem {
  cv_id: string;
  collection_id: string;
  fileName: string;
  createdAt: string;
  summary: string;
  mime?: string;
  user_id?: string;
  upload_time?: string;
  last_update_time?: string;
}

/**
 * Interface for match results returned from the API
 */
export interface MatchResult {
  score: number;
  details: Record<string, number | string>;
}

/**
 * Base URL for the CV automation API
 */
const API_BASE_URL = import.meta.env.VITE_API_URL


/**
 * Parse resume text by calling the API Gateway endpoint with Cognito authorization
 * @param text The text content of the resume
 * @returns A promise that resolves to the generated summary
 */
export const parseCV = async (cv_id: string, collection_id: string): Promise<string> => {
  try {
    // Get Cognito ID token for authorization
    const session = await fetchAuthSession()
    const token = session.tokens?.idToken?.toString()
    if (!token) {
      throw new Error("No Cognito ID token available")
    }

    const queryParams = new URLSearchParams({
      cv_id: cv_id,
      collection_id: collection_id,
    });

    const response = await fetch(`${API_BASE_URL}/parse-cv?${queryParams.toString()}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`,
      },
      body: JSON.stringify({ cv_id: cv_id, collection_id: collection_id }),
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`API request failed: ${response.status} - ${errorText}`)
    }

    const data = await response.json()
    if (data.error) {
      throw new Error(data.error)
    }
    return data.cv || "No summary generated"
  } catch (error) {
    console.error("Error calling summarize API:", error)
    throw error
  }
}

/**
 * Parse a job description by calling the API Gateway endpoint with Cognito authorization
 * @param text The text content of the resume
 * @returns A promise that resolves to the generated summary
 */
export const parseJD = async (jd_id: string, collection_id: string): Promise<string> => {
  try {
    // Get Cognito ID token for authorization
    const session = await fetchAuthSession()
    const token = session.tokens?.idToken?.toString()
    if (!token) {
      throw new Error("No Cognito ID token available")
    }

    const queryParams = new URLSearchParams({
      jd_id: jd_id,
      collection_id: collection_id,
    });

    const response = await fetch(`${API_BASE_URL}/parse-jd?${queryParams.toString()}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`,
      },
      body: JSON.stringify({ jd_id: jd_id, collection_id: collection_id }),
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`API request failed: ${response.status} - ${errorText}`)
    }

    const data = await response.json()
    if (data.error) {
      throw new Error(data.error)
    }
    return data.jd || "No summary generated"
  } catch (error) {
    console.error("Error calling summarize API:", error)
    throw error
  }
}


/**
 * Generate a summary from resume text by calling the API Gateway endpoint with Cognito authorization
 * @param text The text content of the resume
 * @returns A promise that resolves to the generated summary
 */
export const generateSummary = async (cv_id: string, collection_id: string): Promise<string> => {
  try {
    // Get Cognito ID token for authorization
    const session = await fetchAuthSession()
    const token = session.tokens?.idToken?.toString()
    if (!token) {
      throw new Error("No Cognito ID token available")
    }

    // Get the model, prompt, and system prompt from localStorage (saved by AdminPanel)
    const model = localStorage.getItem('cv-selected-model') || "bedrock/apac.anthropic.claude-3-5-sonnet-20240620-v1:0";
    const summaryPrompt = localStorage.getItem('summary-prompt') || "";
    
    console.log("Using AI settings from Admin panel:", { model, summaryPrompt: summaryPrompt.substring(0, 50) + "..." });

    const queryParams = new URLSearchParams({
      cv_id: cv_id,
      collection_id: collection_id,
    });

    const response = await fetch(`${API_BASE_URL}/summarize-cv?${queryParams.toString()}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`,
      },
      body: JSON.stringify({ 
        model: model, 
        prompt: summaryPrompt
      }),
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`API request failed: ${response.status} - ${errorText}`)
    }

    const data = await response.json()
    if (data.error) {
      throw new Error(data.error)
    }
    return data.summary || "No summary generated"
  } catch (error) {
    console.error("Error calling summarize API:", error)
    throw error
  }
}

/**
 * Upload a resume file to S3 using a presigned URL
 * @param file The resume file to upload
 * @param options Optional parameters for the upload
 * @returns A promise that resolves to the file ID or null
 */
export const uploadResumeFile = async (
  file: File,
  options?: {
    resumeCollectionId?: string
    targetContentType?: string
    originalFilename?: string
  }
): Promise<[string | null, string]> => {
  try {
    // Get Cognito ID token for authorization
    const session = await fetchAuthSession()
    const token = session.tokens?.idToken?.toString()
    if (!token) {
      throw new Error("No Cognito ID token available")
    }

    // Step 1: Request a presigned URL from the API
    // Construct URL with query parameters
    // Extract loginId from username (email) and use as user_id
    const username = session?.tokens?.idToken?.payload?.email || "";
    const loginId = typeof username === 'string' ? username.split('@')[0] : "anonymous";

    const queryParams = new URLSearchParams({
      user_id: loginId,
      collection_id: options?.resumeCollectionId || "scoville",
      mime_type: options?.targetContentType || "application/pdf",
      file_name: options?.originalFilename || file.name,
      file_ext: file.name.split('.').pop() || 'pdf', // Default to pdf if no extension
    });

    const collection_id = options?.resumeCollectionId || "scoville";
    
    // Check if API_BASE_URL is defined
    if (!API_BASE_URL) {
      console.error("API_BASE_URL is undefined. Check your environment variables.");
      throw new Error("API base URL is not configured properly. Check your environment variables.");
    }
    
    console.log("Using API URL:", API_BASE_URL);
    const url = `${API_BASE_URL}/get-presigned-url?${queryParams.toString()}`;
    
    const presignedUrlResponse = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`,
      },
    })

    if (!presignedUrlResponse.ok) {
      const errorText = await presignedUrlResponse.text()
      throw new Error(`Failed to get presigned URL: ${presignedUrlResponse.status} - ${errorText}`)
    }

    const presignedData = await presignedUrlResponse.json()
    const presignedUrl = presignedData.data.s3_url
    const cv_id = presignedData.data.cv_id || null;
    
    console.log("Received presigned URL:", presignedData.data.s3_url, "cv_id:", cv_id);  
    
    // Check if presignedUrl is defined
    if (!presignedUrl) {
      console.error("Received response from server but presignedUrl is undefined:", presignedData);
      throw new Error("Server did not provide a valid presigned URL for upload");
    }

    // Step 2: Use the presigned URL to upload the file directly to S3
    // Convert File object to ArrayBuffer for reliable upload
    const fileBuffer = await file.arrayBuffer();
    const fileSize = file.size;
    
    console.log(`📄 File size: ${fileSize} bytes`);
    
    const uploadResponse = await fetch(presignedUrl, {
      method: "PUT",
      headers: {
        "Content-Type": options?.targetContentType || "application/pdf",
        "Content-Length": fileSize.toString(),
      },
      body: new Uint8Array(fileBuffer), // Use Uint8Array for binary data upload
    })

    if (!uploadResponse.ok) {
      throw new Error(`Failed to upload file to S3: ${uploadResponse.status}`)
    }

    // parse the CV to ensure it's processed
    console.log("Parsing CV before matching...");
    await parseCV(cv_id || "", collection_id);

    // Return the file ID from the presigned data if available
    return [presignedData.data.cv_id || null, collection_id]
  } catch (error) {
    console.error("Error uploading resume file:", error)
    throw error
  }
}


/**
 * Upload a resume file to S3 using a presigned URL
 * @param file The resume file to upload
 * @param options Optional parameters for the upload
 * @returns A promise that resolves to the file ID or null
 */
export const uploadJobDescription = async (
  file: File,
  options?: {
    resumeCollectionId?: string
  }
): Promise<[string | null, string]> => {
  try {
    // Get Cognito ID token for authorization
    const session = await fetchAuthSession()
    const token = session.tokens?.idToken?.toString()
    if (!token) {
      throw new Error("No Cognito ID token available")
    }

    // Step 1: Request a presigned URL from the API
    // Construct URL with query parameters
    // Extract loginId from username (email) and use as user_id
    const username = session?.tokens?.idToken?.payload?.email || "";
    const loginId = typeof username === 'string' ? username.split('@')[0] : "anonymous";

    const queryParams = new URLSearchParams({
      user_id: loginId,
      collection_id: options?.resumeCollectionId || "scoville",
    });

    const collection_id = options?.resumeCollectionId || "scoville";
    
    // Check if API_BASE_URL is defined
    if (!API_BASE_URL) {
      console.error("API_BASE_URL is undefined. Check your environment variables.");
      throw new Error("API base URL is not configured properly. Check your environment variables.");
    }
    
    console.log("Using API URL:", API_BASE_URL);
    const url = `${API_BASE_URL}/get-jd-presigned-url?${queryParams.toString()}`;
    
    const presignedUrlResponse = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`,
      },
    })

    if (!presignedUrlResponse.ok) {
      const errorText = await presignedUrlResponse.text()
      throw new Error(`Failed to get presigned URL: ${presignedUrlResponse.status} - ${errorText}`)
    }

    const presignedData = await presignedUrlResponse.json()
    const presignedUrl = presignedData.data.s3_url
    const jd_id = presignedData.data.jd_id || null;
    
    console.log("Received presigned URL:", presignedData.data.s3_url, "jd_id:", jd_id);  
    
    // Check if presignedUrl is defined
    if (!presignedUrl) {
      console.error("Received response from server but presignedUrl is undefined:", presignedData);
      throw new Error("Server did not provide a valid presigned URL for upload");
    }

    // Step 2: Use the presigned URL to upload the file directly to S3
    // Convert File object to ArrayBuffer for reliable upload
    const fileBuffer = await file.arrayBuffer();
    const fileSize = file.size;
    
    console.log(`📄 File size: ${fileSize} bytes`);
    
    const uploadResponse = await fetch(presignedUrl, {
      method: "PUT",
      headers: {
        "Content-Type": "text/plain", // Assuming job descriptions are text files
        "Content-Length": fileSize.toString(),
      },
      body: new Uint8Array(fileBuffer), // Use Uint8Array for binary data upload
    })

    if (!uploadResponse.ok) {
      throw new Error(`Failed to upload file to S3: ${uploadResponse.status}`)
    }

    // parse the JD to ensure it's processed
    console.log("Parsing Job Description ...");
    await parseJD(jd_id, collection_id);

    // Return the file ID from the presigned data if available
    return [presignedData.data.jd_id || null, collection_id]
  } catch (error) {
    console.error("Error uploading resume file:", error)
    throw error
  }
}

/**
 * Fetch all CV summaries for the current user
 * @returns A promise that resolves to an array of summaries
 */
export const getAllSummaries = async () => {
  try {
    // Get Cognito ID token for authorization
    const session = await fetchAuthSession()
    const token = session.tokens?.idToken?.toString()
    if (!token) {
      throw new Error("No Cognito ID token available")
    }

    // Extract loginId from username (email) and use as user_id
    const username = session?.tokens?.idToken?.payload?.email || "";
    const loginId = typeof username === 'string' ? username.split('@')[0] : "anonymous";
    
    const queryParams = new URLSearchParams({
      user_id: loginId,
    });

    const response = await fetch(`${API_BASE_URL}/get-cv-collection?${queryParams.toString()}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`,
      },
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`API request failed: ${response.status} - ${errorText}`)
    }

    const data = await response.json()
    
    // Log the first item to see the timestamp format from the API
    if (data.collection && data.collection.length > 0) {
      console.log("API timestamp format example:", {
        upload_time: data.collection[0].upload_time,
        last_update_time: data.collection[0].last_update_time
      });
    }
    
    // Transform the collection into the format expected by the SummariesPage component
    const summaries: SummaryItem[] = (data.collection || []).map((item: CvItem) => ({
      cv_id: item.cv_id,
      collection_id: item.collection_id,
      fileName: item.original_file_name,
      createdAt: item.upload_time,
      summary: "", // Empty string initially, summaries will be loaded when viewing
      mime: item.mime,
      user_id: item.user_id,
      upload_time: item.upload_time,
      last_update_time: item.last_update_time
    }));
    
    return summaries;
  } catch (error) {
    console.error("Error fetching summaries:", error)
    throw error
  }
}

/**
 * Fetch a CV by ID
 * @param cvId The ID of the CV to fetch
 * @returns A promise that resolves to the CV content URL with expiry information
 */
export const getCvById = async (cvId: string, collection_id: string) => {
  try {
    // Get Cognito ID token for authorization
    const session = await fetchAuthSession()
    const token = session.tokens?.idToken?.toString()
    if (!token) {
      throw new Error("No Cognito ID token available")
    }

     const queryParams = new URLSearchParams({
      cv_id: cvId,
      collection_id: collection_id || "scoville"
    });

    const response = await fetch(`${API_BASE_URL}/get-cv?${queryParams.toString()}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`,
      },
      // Added cache: 'no-cache' to ensure we always get a fresh URL
      cache: 'no-cache'
    })

   if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Failed to get presigned URL: ${response.status} - ${errorText}`)
    }

    const presignedData = await response.json()
    console.log("Fetched presigned data:", presignedData)
    const presignedUrl = presignedData.s3_url
    
    // Add timestamp to track when we received this URL
    return {
      url: presignedUrl || null,
      timestamp: Date.now(),
      // Pre-signed URLs typically expire in 15 minutes to 1 hour, using 15 mins as safe default
      expiresInMs: 15 * 60 * 1000 
    }
  } catch (error) {
    console.error("Error fetching CV:", error)
    throw error
  }
}

/**
 * Fetch a summary by CV ID
 * @param cvId The ID of the CV for which to fetch the summary
 * @returns A promise that resolves to the summary text
 */
export const getSummaryByCvId = async (cvId: string, collection_id: string) => {
  try {
    // Get Cognito ID token for authorization
    const session = await fetchAuthSession()
    const token = session.tokens?.idToken?.toString()
    if (!token) {
      throw new Error("No Cognito ID token available")
    }
    
    const queryParams = new URLSearchParams({
      cv_id: cvId,
      collection_id: collection_id || "scoville"
    });
    const response = await fetch(`${API_BASE_URL}/get-summary?${queryParams.toString()}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`,
      },
    })

   if (!response.ok) {
      return null
      // const errorText = await response.text()
      // throw new Error(`Failed to get presigned URL: ${response.status} - ${errorText}`)
    }

    const data = await response.json()
    console.log("Fetched summary data:", data)
    return data.summary || null
  } catch (error) {
    console.error("Error fetching summary:", error)
    throw error
  }
}

/**
 * Match a CV with a job description
 * @param cvId The ID of the CV to match
 * @param jdId The ID of the job description to match against
 * @param collectionId The collection ID of the CV
 * @returns A promise that resolves to the match results
 */
export const matchCvWithJobDescription = async (
  cvId: string, 
  jdId: string, 
  collectionId: string
): Promise<MatchResult> => {
  try {
    // Get Cognito ID token for authorization
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();
    if (!token) {
      throw new Error("No Cognito ID token available");
    }

    // Now proceed with matching
    const queryParams = new URLSearchParams({
      cv_id: cvId,
      jd_id: jdId,
      collection_id: collectionId || "scoville"
    });

    const model = localStorage.getItem('cv-selected-model') || "bedrock/apac.anthropic.claude-3-5-sonnet-20240620-v1:0";
    const matchPrompt = ""; // localStorage.getItem('match-prompt') || "";

    const response = await fetch(`${API_BASE_URL}/match-cv?${queryParams.toString()}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`,
      },
      body: JSON.stringify({ 
        model: model, 
        prompt: matchPrompt
      }),
    })

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`API request failed: ${response.status} - ${errorText}`);
    }

    const data = await response.json();
    if (data.error) {
      throw new Error(data.error);
    }

    return {
      score: data.match_result?.atsScore || 0,
      details: data.match_result || {}
    };
  } catch (error) {
    console.error("Error matching CV with job description:", error);
    throw error;
  }
}
